import { useMutation } from 'hooks/useMutation'
import { useQuery } from 'hooks/useQuery'
import { useQueryClient } from 'hooks/useQueryClient'

import { createProgramRoom } from 'utils/queries'
import {
  createStripeCheckoutSession,
  createStripeProgram,
  fetchAccount,
  fetchAccountLink,
  fetchCustomer,
  fetchCustomerPortalLink,
} from 'utils/queries/stripe'

import {
  StripeAccount,
  StripeAccountLink,
  StripeCustomer,
  StripeGeneratedLink,
  StripeProgramInput,
} from 'types/stripe'

export const useStripe = () => {
  const queryClient = useQueryClient()
  const account = useQuery<StripeAccount>(['stripeAccount'], () => fetchAccount())
  const customer = useQuery<StripeCustomer>(['stripeCustomer'], () => fetchCustomer())

  const accountDataDetails = account.data?.details_submitted
  const accountLink = useQuery<StripeAccountLink>(
    ['stripeAccountLink', accountDataDetails],
    () => fetchAccountLink(accountDataDetails ? 'login' : 'onboarding'),
    {
      enabled: !!account.data,
    },
  )

  const createProgram = useMutation<undefined, StripeProgramInput>(
    (data: StripeProgramInput) => {
      return createStripeProgram(data)
    },
    {
      onSuccess: (_, variables) => {
        createProgramRoom({ programId: variables.programId })
        queryClient.invalidateQueries('rooms', { refetchInactive: true })
        queryClient.invalidateQueries(['programs'], { refetchInactive: true })
      },
    },
  )

  const createCheckoutSession = useMutation<
    StripeGeneratedLink,
    { priceId?: string; trainerId?: string }
  >(({ priceId, trainerId }) => {
    if (!trainerId || !priceId) throw new Error('Missing trainerId or priceId')

    return createStripeCheckoutSession({ priceId, trainerId })
  })

  const customerPortal = useQuery<StripeGeneratedLink>(
    ['stripeCustomerPortalLink'],
    () => fetchCustomerPortalLink(),
    {
      enabled: !!customer.data,
    },
  )

  return {
    customer,
    account,
    accountLink,
    createProgram,
    customerPortal,
    createCheckoutSession,
  }
}
